'use client';

import Image from '@/components/Image';
import Waterfall from '@/components/Waterfall';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { useStore } from '@/providers/app-store-provider';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';
import { useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { usePersonalityFitTracking } from '../hooks/usePersonalityFitTracking';

interface WaterItem {
  type: number; // 1/3 banner, 2 product
  jump_type?: number;
  jump_value?: string;
  adver_img?: string;
  shop_sku?: string;
  business_sku?: string;
  sku?: string;
  image?: { product?: string[] };
  name?: string;
  short_name?: string;
  price_info?: any[];
}

export default function WaterFull({
  waterFull = [],
  bannerThreeData,
}: {
  waterFull?: WaterItem[];
  bannerThreeData?: any;
}) {
  const { navigate } = useAppNavigation();
  const currency = useStore((s) => s.currency);
  const userInfo = useStore((s) => s.userInfo);
  const { trackMktClick, trackMktExp } = usePersonalityFitTracking();

  // 过滤掉商品项中没有 short_name 的数据
  const filteredWaterFull = useMemo(() => {
    return waterFull.filter((item) => {
      // 非商品项（广告位）保留
      if (Number(item.type) !== 2) return true;
      // 商品项：必须有 short_name
      return !!item.short_name;
    });
  }, [waterFull]);

  // Waterfall_Area_0 顶部 Banner 曝光
  const [bannerExpRef, bannerInView] = useInView({ threshold: 0.5, triggerOnce: true });

  useEffect(() => {
    if (bannerInView && bannerThreeData?.image) {
      try {
        trackMktExp({ mkt_name: 'Waterfall_Area_0' });
      } catch {
        /* noop */
      }
    }
  }, [bannerInView, bannerThreeData?.image, trackMktExp]);

  const productImg = (item: WaterItem) => {
    if (item.adver_img) return item.adver_img;
    const list = item?.image?.product || [];
    return list[0] || '';
  };

  // 构建埋点参数
  const buildWaterTrackParams = (item: WaterItem, index: number) => {
    const params: any = { mkt_name: `Waterfall_${index}` };
    const isAd = Number(item.type) === 1 || Number(item.type) === 3;
    const isProduct = Number(item.type) === 2;

    if (isAd) {
      // 广告位：根据 jump_type 判断
      if (Number(item?.jump_type) === 2) {
        params.shop_sku = item?.shop_sku || '';
        params.business_sku = item?.business_sku || '';
        params.product_sku = item?.sku || '';
      } else if (Number(item?.jump_type) === 1) {
        params.group_id = item?.jump_value || '';
      }
    } else if (isProduct) {
      // 商品：直接传递商品信息
      params.shop_sku = item?.shop_sku || '';
      params.business_sku = item?.business_sku || '';
      params.product_sku = item?.sku || '';
    }
    return params;
  };

  const toJump = (data: WaterItem, index: number) => {
    // 列表页跳转需要标记 sessionStorage
    if (Number(data?.jump_type) === 1 && typeof window !== 'undefined' && window.sessionStorage) {
      try {
        window.sessionStorage.setItem('zl_goodsListEnterPageScene', '活动专题页');
      } catch {
        /* noop */
      }
    }
    try {
      trackMktClick(buildWaterTrackParams(data, index));
    } catch {
      /* noop */
    }
    if (Number(data?.jump_type) === 2) navigate(`/goods-detail/${data?.jump_value}`);
    else navigate(`/goods-list/${data?.jump_value}`);
  };

  const toDetail = (item: WaterItem, index: number) => {
    try {
      trackMktClick(buildWaterTrackParams(item, index));
    } catch {
      /* noop */
    }
    if (item?.shop_sku) navigate(`/goods-detail/${item.shop_sku}`);
  };

  // Waterfall_{index} 单项曝光组件
  const WaterfallItemWrapper = ({
    item,
    index,
    children,
  }: {
    item: WaterItem;
    index: number;
    children: React.ReactNode;
  }) => {
    const [itemExpRef, itemInView] = useInView({ threshold: 0.5, triggerOnce: true });

    useEffect(() => {
      if (itemInView) {
        try {
          trackMktExp(buildWaterTrackParams(item, index));
        } catch {
          /* noop */
        }
      }
    }, [itemInView, index]);

    return <div ref={itemExpRef}>{children}</div>;
  };

  return (
    <div className="waterFull px-[3.125vw] py-[2.92vw]">
      {bannerThreeData?.image && (
        <div ref={bannerExpRef} className="banner flex items-center mb-[4.64vw]">
          <Image
            src={bannerThreeData.image}
            alt=""
            width={10}
            height={200}
            className="w-[48.4vw] min-w-[48.4vw] h-auto"
          />
          {bannerThreeData?.word && (
            <div
              className="pl-[1.09vw] text-[#BDAD8D] leading-[1.61vw] text-[0.94vw]"
              dangerouslySetInnerHTML={{
                __html: String(bannerThreeData.word)
                  .replace(
                    new RegExp('<h1>', 'gi'),
                    '<h1 style="display:inline-block;margin:0;font-size:inherit;line-height:inherit;font-weight:inherit;">',
                  )
                  .replace(new RegExp('</h1>', 'gi'), '</h1>'),
              }}
            />
          )}
        </div>
      )}

      {Array.isArray(filteredWaterFull) && filteredWaterFull.length > 0 && (
        <Waterfall<WaterItem>
          data={filteredWaterFull}
          columns={4}
          gap={18}
          responsive
          baseWidth={1440}
          defaultItemWidth={324}
          minHeight={300}
          renderItem={(item: WaterItem, index: number) => {
            const isAd = Number(item.type) === 1 || Number(item.type) === 3;
            const isProduct = Number(item.type) === 2;
            const canJump = !!item.jump_type;

            if (isAd) {
              return (
                <WaterfallItemWrapper item={item} index={index}>
                  <div className="rounded-[1.04vw] overflow-hidden bg-white">
                    {canJump ? (
                      <a
                        href={
                          Number(item.jump_type) === 2
                            ? `/goods-detail/${item.jump_value}`
                            : `/goods-list/${item.jump_value}`
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          toJump(item, index);
                        }}
                      >
                        <Image
                          src={item.adver_img || ''}
                          alt={`zeelool personality frame glasses${index}`}
                          width={800}
                          height={600}
                          className="w-full h-auto"
                        />
                      </a>
                    ) : (
                      <Image
                        src={item.adver_img || ''}
                        alt={`zeelool personality frame glasses${index}`}
                        width={800}
                        height={600}
                        className="w-full h-auto"
                      />
                    )}
                  </div>
                </WaterfallItemWrapper>
              );
            }

            if (isProduct) {
              return (
                <WaterfallItemWrapper item={item} index={index}>
                  <a
                    href={`/goods-detail/${item.shop_sku}`}
                    onClick={(e) => {
                      e.preventDefault();
                      toDetail(item, index);
                    }}
                  >
                    <div className="rounded-[1.04vw] overflow-hidden bg-white">
                      <div
                        className="img-wrap flex items-center justify-center w-full"
                        style={{ aspectRatio: 1 }}
                      >
                        <Image
                          src={productImg(item)}
                          alt={`${item.name || ''}${index}`}
                          width={600}
                          height={600}
                          className="w-full h-auto"
                        />
                      </div>
                      <div className="price-info h-[6.67vw] flex flex-col justify-center items-center">
                        <p className=" text-[1.25vw] text-primary-black text-center">
                          {item.short_name}
                        </p>
                        <div className="price flex items-center justify-center">
                          {(() => {
                            const p = priceInfo(userInfo, item, currency) || {};
                            const now = formatPriceNumberWithSymbol(
                              String(p?.sell_price || p?.price || '0'),
                              currency,
                            );
                            const old =
                              p && p.price && p.sell_price && p.sell_price !== p.price
                                ? formatPriceNumberWithSymbol(String(p.price), currency)
                                : '';
                            return (
                              <>
                                <span className="now-price notranslate text-[1.25vw] font-bb">
                                  {now}
                                </span>
                                {old && (
                                  <span className="old-price notranslate ml-[0.57vw] text-[1.04vw] text-[#787878] line-through">
                                    {old}
                                  </span>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </a>
                </WaterfallItemWrapper>
              );
            }

            return null;
          }}
        />
      )}
    </div>
  );
}
